import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useQuery } from "@tanstack/react-query";
import { fetchJSON } from "@/lib/api";
import {
  fetchActiveFuelProducts,
  fuelProductLabel,
  resolveProductLabel,
  type FuelProductRow,
} from "@/lib/fuelProducts";
import { formatDate, formatDateTime } from "@/lib/dateUtils";

type AggregatedRow = {
  date: string;
  shift: string;
  created_at?: string;
  byProduct: Record<string, { quantity: number; amount: number }>;
};

function productKey(label: string): string {
  return label.toLowerCase().replace(/[^a-z0-9]+/g, "_");
}

function aggregateSaleEntries(entries: any[], products: FuelProductRow[]): AggregatedRow[] {
  const byId = new Map(products.map((p) => [String(p.id), p]));
  const groups = new Map<string, AggregatedRow>();

  for (const e of entries) {
    const date = String(e.sale_date || "").slice(0, 10);
    const shift = e.shift || "S-1";
    if (!date) continue;
    const key = `${date}|${shift}`;
    if (!groups.has(key)) {
      groups.set(key, { date, shift, created_at: e.created_at, byProduct: {} });
    }
    const row = groups.get(key)!;
    const label = resolveProductLabel(e.product_name || "", e.fuel_product_id, products, byId);
    const pk = productKey(label);
    if (!row.byProduct[pk]) row.byProduct[pk] = { quantity: 0, amount: 0 };
    row.byProduct[pk].quantity += Number(e.quantity || 0);
    row.byProduct[pk].amount += Number(e.net_sale_amount || 0);
    if (e.created_at && (!row.created_at || String(e.created_at) > String(row.created_at))) {
      row.created_at = e.created_at;
    }
  }

  return Array.from(groups.values()).sort((a, b) => {
    const d = b.date.localeCompare(a.date);
    if (d !== 0) return d;
    return a.shift.localeCompare(b.shift);
  });
}

export default function DeleteSale() {
  const { toast } = useToast();

  const [searchFrom, setSearchFrom] = useState<string>("");
  const [searchTo, setSearchTo] = useState<string>("");
  const [filterProduct, setFilterProduct] = useState<string>("");

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<{ date: string; shift: string } | null>(null);

  const { data: fuelProducts = [] } = useQuery({
    queryKey: ["/api/fuel-products"],
    queryFn: fetchActiveFuelProducts,
  });

  const productColumns = useMemo(
    () => fuelProducts.map((p) => ({ key: productKey(fuelProductLabel(p)), label: fuelProductLabel(p) })),
    [fuelProducts]
  );

  const { data: saleEntriesData, refetch: refetchSaleEntries, isLoading } = useQuery({
    queryKey: ["/api/sale-entries", searchFrom, searchTo, filterProduct],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (searchFrom) params.append("from", searchFrom);
      if (searchTo) params.append("to", searchTo);
      if (filterProduct && filterProduct !== "All") params.append("product", filterProduct);

      const response = await fetchJSON(`/api/sale-entries?${params.toString()}`);
      const result = await response.json();
      if (!result.ok && !result.success) throw new Error(result.error || "Failed to fetch sale entries");
      return result.rows || [];
    },
  });

  const aggregatedRows = useMemo(() => {
    const entries = saleEntriesData || [];
    let rows = aggregateSaleEntries(entries, fuelProducts);
    if (filterProduct) {
      const q = filterProduct.toLowerCase();
      rows = rows.filter((r) =>
        Object.keys(r.byProduct).some((k) => k.includes(q.replace(/[^a-z0-9]+/g, "_")))
      );
    }
    return rows;
  }, [saleEntriesData, fuelProducts, filterProduct]);

  const openDeleteDialog = (date: string, shift: string) => {
    setSelectedRow({ date, shift });
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedRow) return;

    try {
      const day = selectedRow.date.slice(0, 10);
      const response = await fetchJSON(`/api/sale-entries?from=${day}&to=${day}`);
      const result = await response.json();
      if (!result.ok && !result.success) {
        throw new Error(result.error || "Failed to load entries for delete");
      }

      const ids = (result.rows || [])
        .filter(
          (r: any) =>
            String(r.sale_date || "").slice(0, 10) === day && String(r.shift || "") === selectedRow.shift
        )
        .map((r: any) => String(r.id));

      if (ids.length === 0) {
        toast({ title: "Nothing to delete", description: "No sale entries found for this date and shift." });
        setDeleteDialogOpen(false);
        setSelectedRow(null);
        return;
      }

      const delRes = await fetchJSON("/api/sale-entries-batch", {
        method: "DELETE",
        body: JSON.stringify({ ids }),
      });
      const delResult = await delRes.json();

      if (delResult.ok || delResult.success) {
        toast({ title: "Success", description: `Deleted ${ids.length} sale entry(s).` });
        setDeleteDialogOpen(false);
        setSelectedRow(null);
        refetchSaleEntries();
      } else {
        toast({
          title: "Error",
          description: delResult.error || "Failed to delete sale entries",
          variant: "destructive",
        });
      }
    } catch (err) {
      toast({ title: "Error", description: String(err), variant: "destructive" });
    }
  };

  const formatQtyAmt = (pk: string, row: AggregatedRow) => {
    const cell = row.byProduct[pk];
    if (!cell || (cell.quantity === 0 && cell.amount === 0)) return "—";
    const parts: string[] = [];
    if (cell.quantity) {
      parts.push(
        `Qty - ${cell.quantity.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      );
    }
    if (cell.amount) {
      parts.push(
        `Amt - ${cell.amount.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      );
    }
    return parts.join("\n");
  };

  const colSpan = 4 + productColumns.length;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-2 text-sm">
        <span className="font-semibold">Dashboard</span>
        <span>/</span>
        <span>Sale</span>
        <span>/</span>
        <span>Delete Sale</span>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Dashboard Sale Delete Sale</CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <span className="text-sm">From Date</span>
              <Input type="date" value={searchFrom} onChange={(e) => setSearchFrom(e.target.value)} className="w-40" />
              <span className="text-sm">To Date</span>
              <Input type="date" value={searchTo} onChange={(e) => setSearchTo(e.target.value)} className="w-40" />
              <Button variant="outline" size="sm" onClick={() => refetchSaleEntries()}>
                Search
              </Button>
            </div>
          </div>

          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <span className="text-sm">Filter:</span>
              <Input
                placeholder="Type to filter product..."
                value={filterProduct}
                onChange={(e) => setFilterProduct(e.target.value)}
                className="w-56"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm">Show:</span>
              <Select defaultValue="all">
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>S.No</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Shift</TableHead>
                {productColumns.map((col) => (
                  <TableHead key={col.key}>{col.label}</TableHead>
                ))}
                <TableHead>Created Date</TableHead>
                <TableHead>Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={colSpan} className="text-center text-gray-500">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : aggregatedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={colSpan} className="text-center text-gray-500">
                    No data available. Add some sale entries to see them here.
                  </TableCell>
                </TableRow>
              ) : (
                aggregatedRows.map((row, index) => (
                  <TableRow key={`${row.date}-${row.shift}`}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      {row.date ? formatDate(row.date) : "-"}
                    </TableCell>
                    <TableCell>{row.shift || "-"}</TableCell>
                    {productColumns.map((col) => (
                      <TableCell key={col.key} className="text-xs whitespace-pre-line">
                        {formatQtyAmt(col.key, row)}
                      </TableCell>
                    ))}
                    <TableCell>
                      {row.created_at ? formatDateTime(row.created_at) : "-"}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => openDeleteDialog(row.date, row.shift)}
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            Delete all sale entries for{" "}
            {selectedRow?.date ? formatDate(selectedRow.date) : ""}{" "}
            shift {selectedRow?.shift}?
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
